/**
 * Conversation Intelligence Service
 *
 * API client for the Category Conversation Intelligence System.
 * Handles conversations, opportunities, sentiment, entities, and authorized voices.
 */

import { authFetch } from '@/utils/auth-fetch';

// ============================================================================
// Types
// ============================================================================

export interface Conversation {
  id: string;
  platform: 'reddit' | 'youtube' | 'forum' | 'twitter';
  conversation_url: string;
  conversation_title: string;
  conversation_text_preview?: string;
  author_username?: string;
  posted_at?: string;
  upvotes: number;
  comment_count: number;
  sentiment: 'positive' | 'neutral' | 'negative' | 'mixed';
  sentiment_score: number;
  topics: Array<{ name: string; relevance: number }>;
  participation_opportunity: boolean;
  opportunity_score: number;
  opportunity_type?: string;
  has_expert_signals: boolean;
  harvested_at: string;
  analyzed_at?: string;
  estimated_value?: number;
  value_tier?: '$' | '$$' | '$$$';
}

export interface ParticipationOpportunity {
  id: string;
  platform: string;
  conversation_url: string;
  conversation_title: string;
  conversation_text: string;
  author_username?: string;
  posted_at?: string;
  upvotes: number;
  comment_count: number;
  sentiment: string;
  topics: Array<{ name: string; relevance: number }>;
  questions: Array<{ question: string; type: string; answered: boolean }>;
  opportunity_score: number;
  opportunity_type: string;
  suggested_approach: string;
  harvested_at: string;
  estimated_value?: number;
  value_tier?: '$' | '$$' | '$$$';
}

export interface ExtractedQuestion {
  question: string;
  type: 'how-to' | 'comparison' | 'recommendation' | 'troubleshooting' | 'general';
  answered: boolean;
  conversationId: string;
  platform: string;
  conversationUrl: string;
  conversationTitle: string;
  sentiment: string;
  postedAt?: string;
  upvotes: number;
}

export interface SentimentTrend {
  period_start: string;
  period_type: string;
  total_conversations: number;
  positive_count: number;
  neutral_count: number;
  negative_count: number;
  mixed_count: number;
  avg_sentiment_score: number;
  top_topics: Array<{ name: string; count: number; avg_sentiment: number }>;
  brand_mentions: Array<{ brand: string; count: number; sentiment: string }>;
  competitor_mentions: Array<{ competitor: string; count: number; sentiment: string }>;
  opportunities_count: number;
}

export interface EntityMention {
  entity_type: string;
  entity_name: string;
  normalized_name: string;
  sentiment_toward: string;
  is_competitor: boolean;
  mention_count: number;
  conversation_count: number;
  avg_sentiment: number;
}

export interface Community {
  id: string;
  platform: string;
  identifier: string;
  name: string;
  url?: string;
  relevance_score: number;
  discovered_via: string;
  language: string;
  metadata: Record<string, unknown>;
  last_discovered_at: string;
  status: string;
}

export interface AuthorizedVoice {
  id: string;
  sector_id: number;
  sector_name?: string;
  category_path?: string;
  platform: string;
  username: string;
  display_name?: string;
  profile_url?: string;
  avatar_url?: string;
  expertise_score: number;
  expertise_topics: string[];
  expertise_evidence: string[];
  follower_count?: number;
  total_posts?: number;
  avg_engagement_score?: number;
  verified_status: boolean;
  communication_quality: 'high' | 'medium' | 'low';
  content_style?: string;
  language: string;
  outreach_status: string;
  contacted_at?: string;
  outreach_notes?: string;
  times_seen: number;
  last_active_at?: string;
  created_at: string;
  linked_expert_id?: string;
  linked_expert_name?: string;
}

export interface HarvestJob {
  id: string;
  site_id: string;
  category_id?: number;
  category_path: string;
  platforms: string[];
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  total_discovered: number;
  total_new: number;
  total_duplicates: number;
  total_analyzed: number;
  total_cost: number;
  created_at: string;
}

export interface ExpertStats {
  total_experts: number;
  candidates: number;
  contacted: number;
  interested: number;
  active_collaborations: number;
  declined: number;
  avg_expertise_score: number;
  platforms_count: number;
  youtube_count: number;
  reddit_count: number;
  forum_count: number;
}

export interface OutreachPipeline {
  [status: string]: {
    count: number;
    experts: Array<{
      id: string;
      username: string;
      platform: string;
      expertise_score: number;
      last_outreach_at?: string;
    }>;
  };
}

// ============================================================================
// API Functions
// ============================================================================

// Conversations
export async function fetchConversations(
  siteId: string,
  filters: {
    categoryId?: number;
    platform?: string;
    sentiment?: string;
    hasOpportunity?: boolean;
    hasExpertSignals?: boolean;
    limit?: number;
    offset?: number;
    market?: string;
  } = {}
): Promise<{ conversations: Conversation[]; total: number }> {
  const params = new URLSearchParams({ siteId });
  if (filters.categoryId) params.append('categoryId', String(filters.categoryId));
  if (filters.platform) params.append('platform', filters.platform);
  if (filters.sentiment) params.append('sentiment', filters.sentiment);
  if (filters.hasOpportunity) params.append('hasOpportunity', 'true');
  if (filters.hasExpertSignals) params.append('hasExpertSignals', 'true');
  if (filters.limit) params.append('limit', String(filters.limit));
  if (filters.offset) params.append('offset', String(filters.offset));
  if (filters.market) params.append('market', filters.market);

  const response = await authFetch(`/conversations?${params}`);
  if (!response.ok) throw new Error('Failed to fetch conversations');
  return response.json();
}

// Participation Opportunities
export async function fetchOpportunities(
  siteId: string,
  filters: {
    categoryId?: number;
    minScore?: number;
    opportunityType?: string;
    limit?: number;
    market?: string;
  } = {}
): Promise<{ opportunities: ParticipationOpportunity[] }> {
  const params = new URLSearchParams({ siteId });
  if (filters.categoryId) params.append('categoryId', String(filters.categoryId));
  if (filters.minScore) params.append('minScore', String(filters.minScore));
  if (filters.opportunityType) params.append('opportunityType', filters.opportunityType);
  if (filters.limit) params.append('limit', String(filters.limit));
  if (filters.market) params.append('market', filters.market);

  const response = await authFetch(`/conversations/opportunities?${params}`);
  if (!response.ok) throw new Error('Failed to fetch opportunities');
  return response.json();
}

// Questions
export async function fetchQuestions(
  siteId: string,
  categoryId: number,
  filters: {
    questionType?: string;
    answered?: boolean;
    limit?: number;
  } = {}
): Promise<{ questions: ExtractedQuestion[] }> {
  const params = new URLSearchParams({ siteId });
  if (filters.questionType) params.append('questionType', filters.questionType);
  if (filters.answered !== undefined) params.append('answered', String(filters.answered));
  if (filters.limit) params.append('limit', String(filters.limit));

  const response = await authFetch(`/conversations/questions/${categoryId}?${params}`);
  if (!response.ok) throw new Error('Failed to fetch questions');
  return response.json();
}

// Sentiment Trends
export async function fetchSentimentTrends(
  siteId: string,
  categoryId: number | null,
  periodType: 'daily' | 'weekly' | 'monthly' = 'weekly',
  periods: number = 12
): Promise<{ trends: SentimentTrend[]; summary: { totalConversations: number; avgSentimentScore: number; positivePercent: string; negativePercent: string } }> {
  const params = new URLSearchParams({
    siteId,
    periodType,
    periods: String(periods)
  });

  // Use "all" when categoryId is null to get all conversations
  const categoryParam = categoryId !== null ? String(categoryId) : 'all';
  const response = await authFetch(`/conversations/sentiment/${categoryParam}?${params}`);
  if (!response.ok) throw new Error('Failed to fetch sentiment trends');
  return response.json();
}

// Entities
export async function fetchEntities(
  siteId: string,
  filters: {
    entityType?: string;
    isCompetitor?: boolean;
    limit?: number;
  } = {}
): Promise<{ entities: EntityMention[] }> {
  const params = new URLSearchParams({ siteId });
  if (filters.entityType) params.append('entityType', filters.entityType);
  if (filters.isCompetitor !== undefined) params.append('isCompetitor', String(filters.isCompetitor));
  if (filters.limit) params.append('limit', String(filters.limit));

  const response = await authFetch(`/conversations/entities?${params}`);
  if (!response.ok) throw new Error('Failed to fetch entities');
  return response.json();
}

// Harvest
export async function triggerHarvest(
  siteId: string,
  categoryPath: string,
  options: {
    categoryId?: number;
    platforms?: string[];
    maxResults?: number;
    market?: string;
  } = {}
): Promise<{ jobId: string; results: HarvestJob }> {
  const response = await authFetch('/conversations/harvest', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      siteId,
      categoryPath,
      ...options
    })
  });
  if (!response.ok) throw new Error('Failed to trigger harvest');
  return response.json();
}

export async function fetchHarvestJob(jobId: string): Promise<{ job: HarvestJob }> {
  const response = await authFetch(`/conversations/harvest/${jobId}`);
  if (!response.ok) throw new Error('Failed to fetch harvest job');
  return response.json();
}

// Market Stats
export interface MarketStats {
  markets: Array<{ market: string; count: string }>;
  total: number;
  languageMismatch?: Array<{ market: string; count: string }>;
}

export async function fetchMarketStats(siteId: string): Promise<MarketStats> {
  const response = await authFetch(`/conversations/stats/markets?siteId=${siteId}`);
  if (!response.ok) throw new Error('Failed to fetch market stats');
  return response.json();
}

// Communities
export async function fetchCommunities(
  sectorId: number,
  categoryPath: string
): Promise<{ communities: Community[] }> {
  const encodedPath = encodeURIComponent(categoryPath);
  const response = await authFetch(`/conversations/categories/${sectorId}/${encodedPath}/communities`);
  if (!response.ok) throw new Error('Failed to fetch communities');
  return response.json();
}

export async function refreshCommunities(
  sectorId: number,
  categoryPath: string
): Promise<{ communities: Community[]; refreshed: boolean }> {
  const encodedPath = encodeURIComponent(categoryPath);
  const response = await authFetch(`/conversations/categories/${sectorId}/${encodedPath}/communities/refresh`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to refresh communities');
  return response.json();
}

// Authorized Voices
export async function fetchAuthorizedVoices(
  sectorId: number,
  filters: {
    categoryPath?: string;
    minExpertise?: number;
    outreachStatus?: string;
    platform?: string;
    limit?: number;
    offset?: number;
  } = {}
): Promise<{ experts: AuthorizedVoice[]; total: number }> {
  const params = new URLSearchParams({ sectorId: String(sectorId) });
  if (filters.categoryPath) params.append('categoryPath', filters.categoryPath);
  if (filters.minExpertise) params.append('minExpertise', String(filters.minExpertise));
  if (filters.outreachStatus) params.append('outreachStatus', filters.outreachStatus);
  if (filters.platform) params.append('platform', filters.platform);
  if (filters.limit) params.append('limit', String(filters.limit));
  if (filters.offset) params.append('offset', String(filters.offset));

  const response = await authFetch(`/authorized-voices?${params}`);
  if (!response.ok) throw new Error('Failed to fetch authorized voices');
  return response.json();
}

export async function fetchVoiceDetails(voiceId: string): Promise<{ expert: AuthorizedVoice & { recentConversations: Conversation[] } }> {
  const response = await authFetch(`/authorized-voices/${voiceId}`);
  if (!response.ok) throw new Error('Failed to fetch voice details');
  return response.json();
}

export async function updateVoiceOutreach(
  voiceId: string,
  status: string,
  notes?: string
): Promise<{ expert: AuthorizedVoice }> {
  const response = await authFetch(`/authorized-voices/${voiceId}/outreach`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, notes })
  });
  if (!response.ok) throw new Error('Failed to update outreach status');
  return response.json();
}

export async function logVoiceContact(
  voiceId: string,
  method: string,
  notes: string
): Promise<{ expert: AuthorizedVoice }> {
  const response = await authFetch(`/authorized-voices/${voiceId}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ method, notes })
  });
  if (!response.ok) throw new Error('Failed to log contact');
  return response.json();
}

export async function fetchExpertStats(sectorId: number): Promise<{ stats: ExpertStats }> {
  const response = await authFetch(`/authorized-voices/sectors/${sectorId}/expert-stats`);
  if (!response.ok) throw new Error('Failed to fetch expert stats');
  return response.json();
}

export async function fetchOutreachPipeline(sectorId: number): Promise<{ pipeline: OutreachPipeline }> {
  const response = await authFetch(`/authorized-voices/pipeline?sectorId=${sectorId}`);
  if (!response.ok) throw new Error('Failed to fetch pipeline');
  return response.json();
}

export async function searchExperts(
  sectorId: number,
  query: string,
  limit: number = 20
): Promise<{ experts: AuthorizedVoice[] }> {
  const params = new URLSearchParams({
    sectorId: String(sectorId),
    query,
    limit: String(limit)
  });
  const response = await authFetch(`/authorized-voices/search?${params}`);
  if (!response.ok) throw new Error('Failed to search experts');
  return response.json();
}

// Helper: Platform icons/colors
export const PLATFORM_CONFIG: Record<string, { color: string; icon: string; label: string }> = {
  reddit: { color: 'bg-orange-500', icon: 'MessageSquare', label: 'Reddit' },
  youtube: { color: 'bg-red-500', icon: 'Youtube', label: 'YouTube' },
  forum: { color: 'bg-blue-500', icon: 'MessageCircle', label: 'Forum' },
  twitter: { color: 'bg-sky-500', icon: 'Twitter', label: 'Twitter/X' },
  blog: { color: 'bg-green-500', icon: 'FileText', label: 'Blog' }
};

export const OUTREACH_STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  candidate: { color: 'bg-gray-500', label: 'Candidate' },
  researching: { color: 'bg-blue-500', label: 'Researching' },
  contacted: { color: 'bg-yellow-500', label: 'Contacted' },
  interested: { color: 'bg-green-500', label: 'Interested' },
  negotiating: { color: 'bg-purple-500', label: 'Negotiating' },
  active: { color: 'bg-emerald-500', label: 'Active' },
  declined: { color: 'bg-red-500', label: 'Declined' },
  unresponsive: { color: 'bg-gray-400', label: 'Unresponsive' },
  blacklisted: { color: 'bg-black', label: 'Blacklisted' }
};

export const SENTIMENT_CONFIG: Record<string, { color: string; bgColor: string }> = {
  positive: { color: 'text-green-700', bgColor: 'bg-green-100 text-green-700' },
  neutral: { color: 'text-gray-700', bgColor: 'bg-gray-200 text-gray-700' },
  negative: { color: 'text-red-700', bgColor: 'bg-red-100 text-red-700' },
  mixed: { color: 'text-yellow-700', bgColor: 'bg-yellow-100 text-yellow-700' }
};

// Entity Mentions Detail
export interface EntityMentionDetail {
  entity_id: string;
  entity_type: string;
  entity_name: string;
  sentiment_toward: string;
  context_snippet: string;
  is_competitor: boolean;
  confidence_score: number;
  conversation_id: string;
  platform: string;
  conversation_title: string;
  conversation_text: string;
  conversation_url: string;
  author_username: string;
  upvotes: number;
  comment_count: number;
  posted_at: string;
  conversation_sentiment: string;
  topics: string[];
}

export interface EntityMentionsResponse {
  entityName: string;
  totalMentions: number;
  sentimentBreakdown: {
    positive: number;
    neutral: number;
    negative: number;
    mixed: number;
  };
  mentions: EntityMentionDetail[];
}

export async function fetchEntityMentions(
  siteId: string,
  entityName: string,
  limit: number = 20
): Promise<EntityMentionsResponse> {
  const params = new URLSearchParams({ siteId, limit: String(limit) });
  const response = await authFetch(`/conversations/entities/${encodeURIComponent(entityName)}/mentions?${params}`);
  if (!response.ok) throw new Error('Failed to fetch entity mentions');
  return response.json();
}

// Expert Profile Summary
export interface ExpertProfileSummary {
  profileType: string;
  summary: string;
  keyStrengths: string[];
  collaborationIdeas: string[];
  outreachTip: string;
}

export interface ExpertProfileSummaryResponse {
  expertId: string;
  profileSummary: ExpertProfileSummary;
  generatedAt: string;
}

export async function fetchExpertProfileSummary(
  expertId: string
): Promise<ExpertProfileSummaryResponse> {
  const response = await authFetch(`/authorized-voices/${expertId}/profile-summary`);
  if (!response.ok) throw new Error('Failed to fetch expert profile summary');
  return response.json();
}

// Re-analysis
export interface ReanalyzeResponse {
  success: boolean;
  message: string;
  count: number;
}

export async function triggerReanalysis(
  siteId: string,
  market?: string
): Promise<ReanalyzeResponse> {
  const response = await authFetch('/conversations/reanalyze', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, market })
  });
  if (!response.ok) throw new Error('Failed to trigger re-analysis');
  return response.json();
}

// ============================================================================
// Expert Voice Linking
// ============================================================================

export interface MatchedQuestion {
  id: string;
  text: string;
  source: { type: 'wheel_question' | 'aeo_question'; sourceId: string; wheelName?: string; categoryPath?: string };
  matchScore: number;
  metrics: { searchVolume: number; opportunityScore: number; contentGap: string | null };
  answerType: string | null;
  isUnanswered: boolean;
}

export interface ExpertMatchedQuestionsResponse {
  success: boolean;
  questions: MatchedQuestion[];
  totalMatched: number;
  expertProfileLength: number;
}

export async function linkVoiceToExpert(voiceId: string, expertId: string) {
  const response = await authFetch(`/authorized-voices/${voiceId}/link-expert`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expertId }),
  });
  if (!response.ok) throw new Error('Failed to link voice to expert');
  return response.json();
}

export async function unlinkVoiceFromExpert(voiceId: string) {
  const response = await authFetch(`/authorized-voices/${voiceId}/link-expert`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to unlink voice from expert');
  return response.json();
}

export async function createExpertFromVoice(
  voiceId: string,
  siteId: string,
  overrides?: Record<string, unknown>
) {
  const response = await authFetch(`/authorized-voices/${voiceId}/create-expert`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, overrides }),
  });
  if (!response.ok) throw new Error('Failed to create expert from voice');
  return response.json();
}

export async function fetchMatchingExperts(voiceId: string, siteId: string) {
  const response = await authFetch(
    `/authorized-voices/${voiceId}/matching-experts?siteId=${siteId}`
  );
  if (!response.ok) throw new Error('Failed to fetch matching experts');
  return response.json();
}

export async function fetchExpertMatchedQuestions(
  expertId: string,
  siteId: string,
  options?: { minSimilarity?: number; limit?: number }
): Promise<ExpertMatchedQuestionsResponse> {
  const params = new URLSearchParams({ siteId, expertId });
  if (options?.minSimilarity) params.set('minSimilarity', String(options.minSimilarity));
  if (options?.limit) params.set('limit', String(options.limit));

  const response = await authFetch(`/interviews/expert-matched-questions?${params}`);
  if (!response.ok) throw new Error('Failed to fetch expert-matched questions');
  return response.json();
}
